import React, { useState, useCallback, useEffect } from 'react';
import { Camera } from 'lucide-react';
import { getCuisineIcon } from '@/utils/iconMaps';
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MultiPhotoUploader, PhotoEntry } from './MultiPhotoUploader';
import { PhotoEditorModal } from './PhotoEditorModal';
import { DishLinkStep } from '@/components/submission/DishLinkStep';
import { ReviewMatrix, ReviewRatings } from '@/components/submission/ReviewMatrix';
import { ChipSelector } from '@/components/ui/chip-selector';
import { SubmissionFlow } from '@/components/SubmissionFlow';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Link2, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { compressImage } from '@/utils/imageCompression';

interface PostToEdit {
  id: string;
  content: string | null;
  image_url: string | null;
  image_urls?: string[] | null;
  dish_id?: string | null;
  rating_value?: string | null;
  rating_portion?: string | null;
  rating_taste?: string | null;
  tags?: string[] | null;
  dishes?: { id: string; name: string } | null;
}

interface CreateSnapSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialLinkedDish?: { id: string; name: string; restaurantName?: string; imageUrl?: string | null; cuisine?: string[] | null } | null;
  initialPost?: PostToEdit | null;
}

const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const CreateSnapSheet: React.FC<CreateSnapSheetProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialLinkedDish,
  initialPost,
}) => {
  const isEditMode = !!initialPost;
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Photos — keyed by stable ID, not array index
  const [photos, setPhotos] = useState<PhotoEntry[]>([]);
  const [editingPhotoId, setEditingPhotoId] = useState<string | null>(null);

  // Form fields
  const [serviceType, setServiceType] = useState<'dine_in' | 'takeaway' | null>(null);
  const [linkedDish, setLinkedDish] = useState<{ id: string; name: string; restaurantName?: string; imageUrl?: string | null; cuisine?: string[] | null } | null>(initialLinkedDish ?? null);
  const [ratings, setRatings] = useState<ReviewRatings>({
    ratingValue: null,
    ratingPortion: null,
    ratingTaste: null,
  });
  const [content, setContent] = useState('');
  const [showRatings, setShowRatings] = useState(false);

  // Dietary & allergen
  const [dietaryClaims, setDietaryClaims] = useState<{ id: string; name: string; icon: string | null }[]>([]);
  const [allergens, setAllergens] = useState<{ id: string; name: string; icon: string | null }[]>([]);
  const [selectedDietaryIds, setSelectedDietaryIds] = useState<string[]>([]);
  const [selectedAllergenIds, setSelectedAllergenIds] = useState<string[]>([]);
  const [showDietary, setShowDietary] = useState(false);
  const [showAllergens, setShowAllergens] = useState(false);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmissionFlow, setShowSubmissionFlow] = useState(false);
  const [pendingRestaurantData, setPendingRestaurantData] = useState<{
    name: string;
    place_id: string;
    address?: string;
    lat?: number;
    lng?: number;
  } | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (initialPost) {
        const existingUrls = initialPost.image_urls?.length
          ? initialPost.image_urls
          : initialPost.image_url ? [initialPost.image_url] : [];
        setPhotos(existingUrls.map((url, i) => ({
          id: `existing-${i}-${Date.now()}`,
          localUrl: url,
          uploadedUrl: url,
          isUploading: false,
        })));
        setContent(initialPost.content ?? '');
        if (initialPost.rating_value || initialPost.rating_portion || initialPost.rating_taste) {
          setRatings({
            ratingValue: (initialPost.rating_value ?? null) as ReviewRatings['ratingValue'],
            ratingPortion: (initialPost.rating_portion ?? null) as ReviewRatings['ratingPortion'],
            ratingTaste: (initialPost.rating_taste ?? null) as ReviewRatings['ratingTaste'],
          });
          setShowRatings(true);
        }
        if (initialPost.dishes) {
          setLinkedDish({ id: initialPost.dishes.id, name: initialPost.dishes.name });
        }
        const dietaryIds = (initialPost.tags ?? []).filter((t) => t.startsWith('dietary:')).map((t) => t.replace('dietary:', ''));
        const allergenIds = (initialPost.tags ?? []).filter((t) => t.startsWith('allergen:')).map((t) => t.replace('allergen:', ''));
        setSelectedDietaryIds(dietaryIds);
        setSelectedAllergenIds(allergenIds);
        if (dietaryIds.length > 0) setShowDietary(true);
        if (allergenIds.length > 0) setShowAllergens(true);
      } else {
        setLinkedDish(initialLinkedDish ?? null);
      }
      supabase.from('dietary_claims').select('id, name, icon').then(({ data }) => {
        if (data) setDietaryClaims(data);
      });
      supabase.from('allergens').select('id, name, icon').then(({ data }) => {
        if (data) setAllergens(data);
      });
    }
  }, [isOpen]);

  const resetForm = useCallback(() => {
    setPhotos([]);
    setEditingPhotoId(null);
    setServiceType(null);
    setLinkedDish(initialLinkedDish ?? null);
    setRatings({ ratingValue: null, ratingPortion: null, ratingTaste: null });
    setContent('');
    setShowRatings(false);
    setSelectedDietaryIds([]);
    setSelectedAllergenIds([]);
    setShowDietary(false);
    setShowAllergens(false);
    setIsSubmitting(false);
    setShowSubmissionFlow(false);
    setPendingRestaurantData(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  // ---------------------------------------------------------------------------
  // Photo upload — uses stable photo ID, immune to array reordering/deletion
  // ---------------------------------------------------------------------------
  const uploadPhotoById = async (file: File, photoId: string) => {
    if (!user) return;

    try {
      let processedFile = file;
      if (file.size > 500 * 1024) {
        processedFile = await compressImage(file);
      }

      const fileExt = processedFile.name.split('.').pop() || 'jpg';
      const fileName = `${user.id}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${fileExt}`;

      const { data, error } = await supabase.storage
        .from('post-images')
        .upload(fileName, processedFile, { cacheControl: '3600', upsert: false });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('post-images')
        .getPublicUrl(data.path);

      setPhotos((prev) =>
        prev.map((p) =>
          p.id === photoId ? { ...p, uploadedUrl: publicUrl, isUploading: false } : p
        )
      );
    } catch (err) {
      console.error('[CreateSnapSheet] Photo upload failed:', err);
      toast.error('Failed to upload photo. Please try again.');
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
    }
  };

  const handleAddPhoto = useCallback(
    (file: File) => {
      if (!user) return;
      const id = makeId();
      const localUrl = URL.createObjectURL(file);
      setPhotos((prev) => [
        ...prev,
        { id, localUrl, uploadedUrl: '', isUploading: true },
      ]);
      uploadPhotoById(file, id);
    },
    [user]
  );

  const handleDeletePhoto = useCallback((id: string) => {
    setPhotos((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const handleEditPhoto = useCallback((id: string) => {
    setEditingPhotoId(id);
  }, []);

  const handleEditorSave = useCallback(
    async (blob: Blob, previewUrl: string) => {
      if (!editingPhotoId || !user) return;

      const targetId = editingPhotoId;
      setEditingPhotoId(null);

      // Capture old uploaded URL before clearing it so we can delete it after
      const oldUploadedUrl = photos.find((p) => p.id === targetId)?.uploadedUrl ?? '';

      // Optimistically update the local preview
      setPhotos((prev) =>
        prev.map((p) =>
          p.id === targetId
            ? { ...p, localUrl: previewUrl, uploadedUrl: '', isUploading: true }
            : p
        )
      );

      // Re-upload the edited blob
      try {
        const file = new File([blob], 'edited-photo.jpg', { type: 'image/jpeg' });
        const fileName = `${user.id}/${Date.now()}_edited_${Math.random().toString(36).slice(2, 8)}.jpg`;

        const { data, error } = await supabase.storage
          .from('post-images')
          .upload(fileName, file, { cacheControl: '3600', upsert: false });

        if (error) throw error;

        const { data: { publicUrl } } = supabase.storage
          .from('post-images')
          .getPublicUrl(data.path);

        setPhotos((prev) =>
          prev.map((p) =>
            p.id === targetId ? { ...p, uploadedUrl: publicUrl, isUploading: false } : p
          )
        );

        // Delete the old file after successful upload (fire-and-forget)
        if (oldUploadedUrl && oldUploadedUrl.includes('supabase.co/storage')) {
          const parts = oldUploadedUrl.split('?')[0].split('/');
          const oldPath = `${parts[parts.length - 2]}/${parts[parts.length - 1]}`;
          supabase.storage.from('post-images').remove([oldPath]);
        }
      } catch (err) {
        console.error('[CreateSnapSheet] Edited photo upload failed:', err);
        toast.error('Failed to save edited photo.');
        setPhotos((prev) =>
          prev.map((p) =>
            p.id === targetId ? { ...p, isUploading: false } : p
          )
        );
      }
    },
    [editingPhotoId, user, photos]
  );

  // ---------------------------------------------------------------------------
  // Dish link
  // ---------------------------------------------------------------------------
  const handleDishSelect = useCallback(
    (dishId: string, dishName: string, restaurantName?: string, imageUrl?: string | null, cuisine?: string[] | null) => {
      setLinkedDish({ id: dishId, name: dishName, restaurantName, imageUrl, cuisine });
    },
    []
  );

  const handleAddNewDish = useCallback(
    (restaurantData: { name: string; place_id: string; address?: string; lat?: number; lng?: number }) => {
      setPendingRestaurantData(restaurantData);
      setShowSubmissionFlow(true);
    },
    []
  );

  const handleDishCreated = useCallback((dishId: string, dishName: string) => {
    setLinkedDish({ id: dishId, name: dishName });
    setShowSubmissionFlow(false);
    setPendingRestaurantData(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------
  const allUploaded = photos.length > 0 && photos.every((p) => !p.isUploading && p.uploadedUrl);
  const canSubmit = allUploaded && !!linkedDish && !isSubmitting;

  const handleSubmit = async () => {
    if (!user) { toast.error('Please sign in to post a snap'); return; }
    if (photos.length === 0) { toast.error('Please add at least one photo'); return; }
    if (!allUploaded) { toast.error('Please wait for photos to finish uploading'); return; }
    if (!linkedDish) { toast.error('Please link a dish before posting'); return; }

    const uploadedUrls = photos.map((p) => p.uploadedUrl);
    const tags: string[] = [
      ...selectedDietaryIds.map((id) => `dietary:${id}`),
      ...selectedAllergenIds.map((id) => `allergen:${id}`),
    ];

    setIsSubmitting(true);
    try {
      if (isEditMode && initialPost) {
        const { error } = await supabase
          .from('posts')
          .update({
            content: content.trim() || null,
            image_url: uploadedUrls[0],
            image_urls: uploadedUrls,
            dish_id: linkedDish.id,
            rating_value: ratings.ratingValue || null,
            rating_portion: ratings.ratingPortion || null,
            rating_taste: ratings.ratingTaste || null,
            tags: tags.length > 0 ? tags : null,
          } as any)
          .eq('id', initialPost.id)
          .eq('user_id', user.id);

        if (error) throw error;
        toast.success('Snap updated!');
        queryClient.invalidateQueries({ queryKey: ['snaps'] });
        queryClient.invalidateQueries({ queryKey: ['dish-gallery', linkedDish.id] });
        queryClient.invalidateQueries({ queryKey: ['dish-story-posts', linkedDish.id] });
        if (user) queryClient.invalidateQueries({ queryKey: ['userPosts', user.id] });
      } else {
        // Dedup guard: check if this exact post was already committed in the last 5 minutes
        // (protects against network retries where the server committed but client timed out)
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
        const { data: existing } = await supabase
          .from('posts')
          .select('id')
          .eq('user_id', user.id)
          .eq('dish_id', linkedDish.id)
          .eq('image_url', uploadedUrls[0])
          .gt('created_at', fiveMinutesAgo)
          .limit(1)
          .maybeSingle();

        if (!existing) {
          const { error } = await supabase
            .from('posts')
            .insert({
              user_id: user.id,
              content: content.trim() || null,
              image_url: uploadedUrls[0],
              image_urls: uploadedUrls,
              video_url: null,
              dish_id: linkedDish.id,
              media_type: 'image',
              rating_value: ratings.ratingValue || null,
              rating_portion: ratings.ratingPortion || null,
              rating_taste: ratings.ratingTaste || null,
              tags: tags.length > 0 ? tags : null,
            } as any);

          if (error) throw error;
          toast.success('Snap posted!');
          queryClient.invalidateQueries({ queryKey: ['snaps'] });
          queryClient.invalidateQueries({ queryKey: ['dish-gallery', linkedDish.id] });
          queryClient.invalidateQueries({ queryKey: ['dish-story-posts', linkedDish.id] });
          if (user) queryClient.invalidateQueries({ queryKey: ['userPosts', user.id] });
        } else {
          toast.info('Snap already posted!');
        }
      }

      resetForm();
      onSuccess();
    } catch (err) {
      console.error('[CreateSnapSheet] Submit failed:', err);
      toast.error(`Failed to ${isEditMode ? 'update' : 'post'} snap. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const editingPhoto = editingPhotoId ? photos.find((p) => p.id === editingPhotoId) : null;

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <SheetContent
          side="bottom"
          className="h-[96vh] flex flex-col p-0 rounded-t-2xl border-t border-border [&>button]:hidden"
        >
          {/* Header */}
          <div className="flex-shrink-0 px-4 pt-4 pb-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="w-16" />
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Camera className="h-5 w-5" />
                {isEditMode ? 'Edit Snap' : 'Post a Snap'}
              </h2>
              <div className="w-16 flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>

          {/* Scrollable Body */}
          <div className="flex-1 overflow-y-auto px-4 py-5 space-y-7">

            {/* Photos */}
            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                Photos
              </h3>
              <MultiPhotoUploader
                photos={photos}
                onAdd={handleAddPhoto}
                onEdit={handleEditPhoto}
                onDelete={handleDeletePhoto}
                maxPhotos={5}
              />
            </section>

            {/* Service Type */}
            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                Where did you eat?
              </h3>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setServiceType('dine_in')}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition-all text-sm font-medium',
                    serviceType === 'dine_in'
                      ? 'border-[#cfef00] bg-[#cfef00]/10 text-foreground'
                      : 'border-border bg-background text-muted-foreground hover:border-[#cfef00]/50'
                  )}
                >
                  <span>🍽️</span>
                  <div className="text-left">
                    <p className="font-medium">Dine In</p>
                    <p className="text-xs font-normal opacity-70">Ate at the restaurant</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setServiceType('takeaway')}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition-all text-sm font-medium',
                    serviceType === 'takeaway'
                      ? 'border-[#cfef00] bg-[#cfef00]/10 text-foreground'
                      : 'border-border bg-background text-muted-foreground hover:border-[#cfef00]/50'
                  )}
                >
                  <span>🥡</span>
                  <div className="text-left">
                    <p className="font-medium">Takeaway</p>
                    <p className="text-xs font-normal opacity-70">Ordered to go</p>
                  </div>
                </button>
              </div>
            </section>

            {/* Dish Link */}
            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                Link a Dish <span className="text-xs font-normal normal-case tracking-normal text-muted-foreground">(Required)</span>
              </h3>
              {linkedDish ? (
                <div className="flex items-center justify-between p-3 bg-[#cfef00]/10 border border-[#cfef00]/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    {linkedDish.imageUrl ? (
                      <img
                        src={linkedDish.imageUrl}
                        alt={linkedDish.name}
                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-[#cfef00]/20 flex items-center justify-center flex-shrink-0">
                        <Link2 className="h-5 w-5 text-[#cfef00]" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-foreground">{linkedDish.name}</p>
                      {linkedDish.restaurantName && (
                        <p className="text-xs text-muted-foreground">{linkedDish.restaurantName}</p>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setLinkedDish(null)}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <DishLinkStep
                  onSelect={handleDishSelect}
                  onBack={() => {}}
                  showBack={false}
                  onAddNewDish={handleAddNewDish}
                  serviceType={serviceType}
                />
              )}
            </section>

            {/* Cuisine Type (auto-filled from linked dish) */}
            {linkedDish?.cuisine && linkedDish.cuisine.length > 0 && (
              <section className="space-y-2">
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Cuisine Type</h3>
                <div className="flex flex-wrap gap-2">
                  {linkedDish.cuisine.map((c) => (
                    <div key={c} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted">
                      <span className="text-lg">{getCuisineIcon(c)}</span>
                      <span className="text-sm font-medium capitalize">{c.replace('_', ' ')}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Ratings (collapsible) */}
            <section className="space-y-2">
              <button
                type="button"
                onClick={() => setShowRatings((v) => !v)}
                className="flex items-center justify-between w-full group"
              >
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                  Rate It
                  <span className="ml-2 text-xs font-normal text-muted-foreground normal-case tracking-normal">
                    (Optional)
                  </span>
                </h3>
                {showRatings
                  ? <ChevronUp className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  : <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                }
              </button>

              {showRatings && (
                <ReviewMatrix
                  value={ratings}
                  onChange={(key, val) => setRatings((prev) => ({ ...prev, [key]: val }))}
                />
              )}
            </section>

            {/* Dietary */}
            <section className="space-y-2">
              <button
                type="button"
                onClick={() => setShowDietary((v) => !v)}
                className="flex items-center justify-between w-full group"
              >
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                  Dietary
                  <span className="ml-2 text-xs font-normal text-muted-foreground normal-case tracking-normal">
                    (Optional)
                  </span>
                </h3>
                {showDietary
                  ? <ChevronUp className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  : <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                }
              </button>
              {showDietary && dietaryClaims.length > 0 && (
                <ChipSelector
                  options={dietaryClaims.map((c) => ({ id: c.id, name: c.name, iconKey: c.icon }))}
                  selected={selectedDietaryIds}
                  onChange={setSelectedDietaryIds}
                  type="dietary"
                  colorScheme="green"
                />
              )}
            </section>

            {/* Allergens */}
            <section className="space-y-2">
              <button
                type="button"
                onClick={() => setShowAllergens((v) => !v)}
                className="flex items-center justify-between w-full group"
              >
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                  Allergens
                  <span className="ml-2 text-xs font-normal text-muted-foreground normal-case tracking-normal">
                    (Optional)
                  </span>
                </h3>
                {showAllergens
                  ? <ChevronUp className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  : <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                }
              </button>
              {showAllergens && allergens.length > 0 && (
                <ChipSelector
                  options={allergens.map((a) => ({ id: a.id, name: a.name, iconKey: a.icon }))}
                  selected={selectedAllergenIds}
                  onChange={setSelectedAllergenIds}
                  type="allergen"
                  colorScheme="amber"
                />
              )}
            </section>

            {/* Caption */}
            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                Caption
                <span className="ml-2 text-xs font-normal text-muted-foreground normal-case tracking-normal">
                  (Optional)
                </span>
              </h3>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="What made this snap special?"
                className="min-h-[90px] resize-none"
                maxLength={500}
              />
              <div className="text-xs text-muted-foreground text-right">
                {content.length}/500
              </div>
            </section>

            <div className="h-2" />
          </div>

          {/* Sticky Submit */}
          <div className="flex-shrink-0 px-4 pb-6 pt-3 border-t border-border bg-background">
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="w-full h-12 text-base font-semibold bg-[#cfef00] hover:bg-[#cfef00]/90 text-black disabled:opacity-40"
            >
              {isSubmitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{isEditMode ? 'Saving...' : 'Posting...'}</>
              ) : (
                isEditMode ? 'Save Changes' : 'Share Snap'
              )}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Photo editor — portalled to body inside the component, renders above everything */}
      {editingPhoto && (
        <PhotoEditorModal
          isOpen
          imageSrc={editingPhoto.localUrl}
          onSave={handleEditorSave}
          onCancel={() => setEditingPhotoId(null)}
        />
      )}

      {/* New dish creation flow */}
      {showSubmissionFlow && pendingRestaurantData && (
        <SubmissionFlow
          isOpen
          onClose={() => { setShowSubmissionFlow(false); setPendingRestaurantData(null); }}
          mode="create"
          prefillData={{
            imageUrl: photos[0]?.uploadedUrl,
            restaurantName: pendingRestaurantData.name,
            googlePlaceId: pendingRestaurantData.place_id,
            restaurantAddress: pendingRestaurantData.address,
            restaurantLat: pendingRestaurantData.lat,
            restaurantLng: pendingRestaurantData.lng,
            serviceType: serviceType || undefined,
          }}
          onDishCreated={handleDishCreated}
        />
      )}
    </>
  );
};
